import { type JSX, useEffect, useState } from 'react';
import { AppBar, Box, Button, Divider, Stack, Toolbar, Typography } from '@mui/material';

import {
  acceptInvite,
  apiBaseUrl,
  fetchLicense,
  fetchMe,
  type FetchResult,
  type MeTenant,
  redeemMagicLink,
  signOut,
  signup,
  type SignupResult as SignupApiResult,
  type TeamRole,
} from './api.js';
import { ConfigEditor } from './components/ConfigEditor.js';
import { FleetsList } from './components/FleetsList.js';
import { LicenseDetails } from './components/LicenseDetails.js';
import { LicenseLookup } from './components/LicenseLookup.js';
import { PasswordSignIn } from './components/PasswordSignIn.js';
import { SetPassword } from './components/SetPassword.js';
import { TwoFactorChallenge } from './components/TwoFactorChallenge.js';
import { TwoFactorSetup } from './components/TwoFactorSetup.js';
import { AdminDashboard } from './components/AdminDashboard.js';
import { FleetDashboard } from './dashboard/FleetDashboard.js';
import { SignupForm } from './components/SignupForm.js';
import { SignupResult } from './components/SignupResult.js';
import { Tour } from './tour/Tour.js';
import { POST_SIGNUP_STEPS, POST_SIGNUP_TOUR_ID } from './tour/tours.js';

type Mode = 'lookup' | 'signup' | 'fleets';

/* ─────────────────────────── routing ───────────────────────────
 * Each top-level tab lives at its own URL hash, so the browser history records
 * real in-app locations and the Back / Forward buttons (and the mouse's
 * back/forward side buttons) step between tabs instead of leaving the Portal.
 * Tabs are plain `<a href>` links. The deep-link query params (?signup, ?setpw,
 * ?sso, Stripe ?upgraded/?canceled, ?signin) stay as entry points — they're
 * consumed on mount and translated into the matching hash. */
const MODE_TO_HASH: Record<Mode, string> = { fleets: 'fleets', lookup: 'sign-in', signup: 'start' };
const HASH_TO_MODE: Record<string, Mode> = { fleets: 'fleets', 'sign-in': 'lookup', start: 'signup' };

function parseMode(): Mode {
  const raw = window.location.hash.replace(/^#\/?/, '');
  return HASH_TO_MODE[raw] ?? 'lookup';
}
/** The hash href for a tab (for `<a href>`). */
function modeHref(m: Mode): string {
  return `#${MODE_TO_HASH[m]}`;
}
/** Programmatic navigation — assigning the hash pushes a history entry. */
function goMode(m: Mode): void {
  window.location.hash = MODE_TO_HASH[m];
}
/** Subscribe to the hash; re-renders on any history navigation. */
function useModeRoute(): Mode {
  const [m, setM] = useState<Mode>(() => parseMode());
  useEffect(() => {
    const on = (): void => setM(parseMode());
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return m;
}

type LookupView =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'result'; result: FetchResult };

type SignupView =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'result'; result: SignupApiResult }
  | {
      kind: 'editing';
      tenantId: string;
      /** null for a non-owner admin — the editor authorizes via the cookie. */
      editToken: string | null;
      launcherUrl: string;
      role?: TeamRole;
    };

/**
 * Top-level auth state. `loading` is the initial pass while we wait for
 * GET /me to resolve; we render a tiny spinner during that ~150ms so
 * the Portal doesn't flash the signed-out UI for a signed-in user.
 */
type AuthState =
  | { kind: 'loading' }
  | { kind: 'unauthenticated' }
  | { kind: 'authenticated'; email: string; tenants: MeTenant[]; totpEnabled: boolean };

/** Friendly copy for the `?autherror=<code>` the Google flow can bounce back. */
function googleAuthErrorMessage(code: string): string {
  switch (code) {
    case 'no_account':
      return 'Google hasn’t verified that email address, so we couldn’t sign you in or create an account with it. Verify the email with Google and try again, or use your email and password below.';
    case 'google_unavailable':
      return 'Google sign-in isn’t available right now. Use your email and password, or request a one-time sign-in link below.';
    case 'google_denied':
      return 'Google sign-in was canceled. You can try again or sign in with your email below.';
    case 'google_state':
    case 'google_exchange':
    case 'google_verify':
      return 'Google sign-in couldn’t be completed — please try again. If it keeps happening, sign in with your email below.';
    default:
      return 'Google sign-in didn’t complete. Try again, or sign in with your email below.';
  }
}

export function App(): JSX.Element {
  const [authState, setAuthState] = useState<AuthState>({ kind: 'loading' });
  const routeMode = useModeRoute();
  // `fleets` is only meaningful once signed in; a stray `#fleets` while
  // definitively signed out falls back to the sign-in tab (during the initial
  // `loading` probe we keep it, so a signed-in reload on #fleets doesn't flash).
  const mode: Mode = routeMode === 'fleets' && authState.kind === 'unauthenticated' ? 'lookup' : routeMode;
  const [lookup, setLookup] = useState<LookupView>({ kind: 'idle' });
  const [signupState, setSignupState] = useState<SignupView>({ kind: 'idle' });
  const [initialId, setInitialId] = useState<string>('');
  /** One-shot banner the editor shows after a Stripe redirect lands here. */
  const [editorBanner, setEditorBanner] = useState<
    { kind: 'success' | 'info'; message: string } | null
  >(null);
  /** When set, ConfigEditor opens on this tab (used by ?upgraded / ?canceled). */
  const [editorInitialTab, setEditorInitialTab] = useState<
    'launcher' | 'broadcasts' | 'upgrade' | undefined
  >(undefined);
  /**
   * `?intent=upgrade-pro|upgrade-enterprise` on mount — set by the
   * marketing site's Pricing CTAs. We hold it through the signup
   * flow; when the user clicks "Customize my launcher" on the
   * SignupResult screen, we send them to the Upgrade tab instead of
   * the default Launcher tab so they can hit Buy without one extra
   * navigation.
   */
  const [pendingIntent, setPendingIntent] = useState<'upgrade-pro' | 'upgrade-enterprise' | null>(
    null,
  );
  /**
   * Set/reset-password token from `?setpw=<token>`. While non-null we render
   * the SetPassword screen over everything else; clearing it (success or
   * expiry) returns the user to the normal Portal.
   */
  const [setPwToken, setSetPwToken] = useState<string | null>(null);
  /**
   * True between a password sign-in that returned `mfaRequired` and the TOTP
   * challenge being satisfied. While true we render TwoFactorChallenge over
   * everything else — the session is mfa_pending, so /me would 401 anyway.
   */
  const [awaitingTwoFactor, setAwaitingTwoFactor] = useState(false);
  /**
   * "Verify a license" reveals the license-ID lookup (auto-opened by a
   * `?license=` deep link). Signed-in users always see the lookup tool.
   * Password recovery lives inside PasswordSignIn (its "email me a link"
   * button), so there's no separate toggle here.
   */
  const [showLicenseLookup, setShowLicenseLookup] = useState(false);

  /** Shared landing after a fully-authed sign-in (password done, or 2FA passed). */
  async function landSignedIn(message: string): Promise<void> {
    await refreshMe();
    goMode('fleets');
    setEditorBanner({ kind: 'success', message });
  }

  /**
   * Refresh /me after sign-in / signup / etc. Calls fetchMe and updates
   * authState; returns the new MeTenant[] so callers can act on it
   * immediately (e.g. find the editToken for a tenant the user just
   * upgraded).
   */
  async function refreshMe(): Promise<MeTenant[]> {
    const res = await fetchMe();
    if (res.kind === 'ok') {
      setAuthState({
        kind: 'authenticated',
        email: res.email,
        tenants: res.tenants,
        totpEnabled: res.totpEnabled,
      });
      return res.tenants;
    }
    setAuthState({ kind: 'unauthenticated' });
    return [];
  }

  // Inspect the URL once on mount and probe /me. Six deep-link cases:
  //   ?license=<id>                 → pre-fill the lookup field (Stripe-receipt path)
  //   ?signup                       → pre-select the signup mode
  //   ?signup&intent=upgrade-pro    → signup, then editor on Upgrade tab
  //   ?upgraded=1&tenant=…          → post-Stripe-Checkout success bounce
  //   ?canceled=1&tenant=…          → post-Stripe-Checkout cancel bounce
  //   ?signin=<token>               → magic-link redemption (sets session cookie)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // ?setpw=<token> — set/reset-password link. Unlike magic-link, we DON'T
    // redeem on mount: the SetPassword screen collects the new password and
    // POSTs token+password together. Capture the token, strip it from the
    // URL (so a reload doesn't resurface it), and render that screen.
    const setpwToken = params.get('setpw');
    if (setpwToken !== null && setpwToken.length > 0) {
      window.history.replaceState({}, '', window.location.pathname);
      setSetPwToken(setpwToken);
      void refreshMe();
      return;
    }

    // ?invite=<token> — team invite. Accept it (server creates the account if
    // new + sets the session cookie), then load /me and land on fleets.
    const inviteToken = params.get('invite');
    if (inviteToken !== null && inviteToken.length > 0) {
      window.history.replaceState({}, '', window.location.pathname);
      void acceptInvite(inviteToken).then(async (res) => {
        await refreshMe();
        if (res.kind === 'ok') {
          goMode('fleets');
          setEditorBanner({
            kind: 'success',
            message: '✓ Invite accepted. Your fleet is below.',
          });
        } else {
          goMode('lookup');
          setLookup({
            kind: 'result',
            result: {
              kind: 'malformed',
              message:
                res.kind === 'invalid'
                  ? 'That invite link has expired or already been used. Ask the fleet owner to re-invite you.'
                  : 'Something went wrong accepting the invite. Please try again.',
            },
          });
        }
      });
      return;
    }

    // ?sso=google[&mfa=1] — landing back from the Google OAuth round-trip.
    // The server has already set the session cookie; we just decide where to
    // go. With &mfa=1 the session is mfa_pending (TOTP enabled), so /me would
    // 401 — show the challenge instead of probing.
    const sso = params.get('sso');
    if (sso === 'google') {
      const mfaPending = params.get('mfa') === '1';
      window.history.replaceState({}, '', window.location.pathname);
      if (mfaPending) {
        setAuthState({ kind: 'unauthenticated' });
        setAwaitingTwoFactor(true);
        return;
      }
      void refreshMe().then((tenants) => {
        goMode(tenants.length > 0 ? 'fleets' : 'lookup');
        setEditorBanner({
          kind: 'success',
          message: '✓ Signed in with Google. Your fleets are below.',
        });
      });
      return;
    }

    // ?autherror=<code> — a Google sign-in that didn't complete. Strip it,
    // probe /me (the user may still have an existing session), and surface a
    // friendly reason on the lookup tab.
    const authError = params.get('autherror');
    if (authError !== null) {
      window.history.replaceState({}, '', window.location.pathname);
      void refreshMe().then(() => {
        goMode('lookup');
        setLookup({
          kind: 'result',
          result: { kind: 'malformed', message: googleAuthErrorMessage(authError) },
        });
      });
      return;
    }

    // ?signin=<token> — magic-link redemption. The server sets the
    // session cookie on success; we then probe /me to populate fleets.
    const signinToken = params.get('signin');
    if (signinToken !== null && signinToken.length > 0) {
      // Strip the token from the URL so a reload doesn't re-attempt
      // redemption (which would 410 since the token is single-use).
      window.history.replaceState({}, '', window.location.pathname);
      void redeemMagicLink(signinToken).then(async (res) => {
        if (res.kind === 'ok') {
          await refreshMe();
          goMode('fleets');
          setEditorBanner({
            kind: 'success',
            message: '✓ Signed in. Your fleets are below.',
          });
        } else if (res.kind === 'expired-or-used') {
          await refreshMe();
          goMode('lookup');
          setLookup({
            kind: 'result',
            result: {
              kind: 'malformed',
              message:
                'That sign-in link has expired or already been used. Request a fresh one below.',
            },
          });
        } else if (res.kind === 'not-found') {
          await refreshMe();
          goMode('lookup');
          setLookup({
            kind: 'result',
            result: {
              kind: 'malformed',
              message: "That sign-in link isn't recognized. Request a fresh one below.",
            },
          });
        } else {
          await refreshMe();
          goMode('lookup');
          setLookup({
            kind: 'result',
            result: {
              kind: 'network',
              message:
                res.kind === 'network'
                  ? res.message
                  : `Sign-in failed (status ${String(res.kind === 'unexpected' ? res.status : 'unknown')}).`,
            },
          });
        }
      });
      return;
    }

    // Probe /me on first paint. If the user is signed in, default to
    // the fleets tab.
    void refreshMe().then((tenants) => {
      const upgraded = params.get('upgraded');
      const canceled = params.get('canceled');
      const tenantId = params.get('tenant');
      if ((upgraded === '1' || canceled === '1') && tenantId !== null) {
        // Strip the Stripe query and land on this fleet's Subscription section
        // in the dashboard — the tier reflects the new plan after the /me
        // refresh above. (replaceState drops the query + sets the hash in one go;
        // dispatch a hashchange so an already-mounted dashboard follows it.)
        if (tenants.some((x) => x.tenantId === tenantId)) {
          window.history.replaceState(
            {},
            '',
            `${window.location.pathname}#fleet/${encodeURIComponent(tenantId)}/subscription`,
          );
          window.dispatchEvent(new HashChangeEvent('hashchange'));
        } else {
          window.history.replaceState({}, '', window.location.pathname);
          goMode('fleets');
        }
        return;
      }
      // No deep link — pick the default tab, but only when the URL has no
      // explicit hash (so a bookmarked/back-navigated #sign-in etc. is
      // respected). Signed-in users with a fleet land on #fleets; everyone
      // else stays on the default sign-in/lookup tab.
      if (window.location.hash === '' && tenants.length > 0) {
        goMode('fleets');
      }
    });

    const id = params.get('license');
    if (id !== null) {
      setInitialId(id);
      setShowLicenseLookup(true);
      goMode('lookup');
      return;
    }
    if (params.has('signup')) {
      goMode('signup');
      const intent = params.get('intent');
      if (intent === 'upgrade-pro' || intent === 'upgrade-enterprise') {
        setPendingIntent(intent);
      }
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signedIn = authState.kind === 'authenticated';
  // Whether the signed-in account already owns at least one fleet. Drives the
  // "Start free" tab: once you have a launcher (every Google SSO sign-in now
  // provisions one), the signup CTA is redundant and hides. A signed-in
  // account with zero fleets (rare — e.g. a Stripe customer pre-launcher)
  // still sees it so the fleets-empty state has somewhere to point.
  const hasFleets = authState.kind === 'authenticated' && authState.tenants.length > 0;

  async function handleLookupSubmit(licenseId: string): Promise<void> {
    setLookup({ kind: 'busy' });
    const result = await fetchLicense(licenseId);
    setLookup({ kind: 'result', result });
  }

  async function handleSignupSubmit(input: {
    email: string;
    companyName: string;
    turnstileToken?: string | null;
  }): Promise<void> {
    setSignupState({ kind: 'busy' });
    const result = await signup(input);
    setSignupState({ kind: 'result', result });
    // The signup endpoint doesn't sign the customer in — they still need
    // to click the magic link in their email. But if they're already
    // signed in (adding a second tenant under the same account),
    // refresh /me so the new tenant shows up immediately.
    if (result.kind === 'ok' && signedIn) {
      void refreshMe();
    }
  }

  async function handleSignOut(): Promise<void> {
    await signOut();
    setAuthState({ kind: 'unauthenticated' });
    setSignupState({ kind: 'idle' });
    goMode('lookup');
    setEditorBanner(null);
  }

  const showLookupResult = mode === 'lookup' && lookup.kind === 'result' && lookup.result.kind === 'ok';
  // Signed-out shows sign-in + start-free on one page, so the signup flow is
  // driven by signupState alone (not the `signup` tab, which only exists for
  // signed-in accounts). `signupFlow` gates the result/editor screens for both.
  const signupFlow = mode === 'signup' || !signedIn;
  const showSignupResult = signupFlow && signupState.kind === 'result' && signupState.result.kind === 'ok';
  const showEditor = signupFlow && signupState.kind === 'editing';
  const editorOpen = signupState.kind === 'editing';

  // Admin console lives on its own subdomain — admin.mobilauncher.com (any
  // `admin.` host, so staging works too). The legacy `?admin=1` query param is
  // kept as a fallback (no customer-facing nav link either way) so local dev —
  // where the subdomain isn't reachable — and any bookmarked link still work.
  // The ADMIN_BEARER_TOKEN is the real security boundary; this only decides
  // which surface renders.
  const adminMode =
    typeof window !== 'undefined' &&
    (window.location.hostname.startsWith('admin.') ||
      new URLSearchParams(window.location.search).get('admin') === '1');

  if (adminMode) {
    // Full-viewport operator console — its own sidebar + top-bar chrome,
    // not the narrow customer-portal shell.
    return <AdminDashboard />;
  }

  // Signed-in customers with at least one fleet get the full-viewport MUI
  // dashboard (its own chrome). Mid-auth overlays (2FA challenge, set-password)
  // and the zero-fleet case fall through to the classic shell below — that's
  // where "create your first fleet" / signup lives.
  if (
    authState.kind === 'authenticated' &&
    authState.tenants.length > 0 &&
    !awaitingTwoFactor &&
    setPwToken === null
  ) {
    return (
      <FleetDashboard
        email={authState.email}
        tenants={authState.tenants}
        totpEnabled={authState.totpEnabled}
        onSignOut={() => void handleSignOut()}
        onRefresh={refreshMe}
      />
    );
  }

  return (
    <div className="portal">
      <AppBar position="static">
        <Toolbar variant="dense">
          <Box
            component="a"
            href="/"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, color: 'inherit', textDecoration: 'none' }}
          >
            <Box component="span" aria-hidden sx={{ color: 'secondary.main', fontSize: 20 }}>▦</Box>
            <Typography variant="subtitle1" fontWeight={700}>MobiLauncher</Typography>
          </Box>
          {authState.kind === 'authenticated' ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">{authState.email}</Typography>
              <Button size="small" color="inherit" onClick={() => void handleSignOut()} data-testid="portal-sign-out">
                Sign out
              </Button>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">Customer portal</Typography>
          )}
        </Toolbar>
      </AppBar>

      <main
        className={`portal__main${
          editorOpen
            ? ' portal__main--editor'
            : mode === 'fleets'
              ? ' portal__main--fleets'
              : ''
        }`}
      >
        {/* Signed-out visitors get the single email-first gate (no tabs).
            Signed-in accounts keep the tab bar — fleets / license lookup, plus
            "Start free" for the rare signed-in-but-fleetless case. */}
        {signedIn && (
          <div className="portal__tabs" role="tablist" aria-label="Portal mode">
            <ModeTab mode="fleets" active={mode === 'fleets'}>
              Your fleets
            </ModeTab>
            <ModeTab mode="lookup" active={mode === 'lookup'}>
              I have a license
            </ModeTab>
            {!hasFleets && (
              <ModeTab mode="signup" active={mode === 'signup'}>
                Start free
              </ModeTab>
            )}
          </div>
        )}

        <section className="portal__hero">
          <h1 className="portal__title">
            {!signedIn
              ? 'Welcome to MobiLauncher'
              : mode === 'fleets'
                ? 'Your fleets'
                : 'Look up a license'}
          </h1>
          <p className="portal__sub">
            {!signedIn
              ? 'Sign in to manage your fleets, or start a free one in under a minute — no card required.'
              : mode === 'fleets'
                ? 'Tenants attached to your account. Open the launcher or jump into the customizer.'
                : 'Look up any license by ID — useful for verifying a customer\'s purchase email.'}
          </p>
        </section>

        {authState.kind === 'loading' && (
          <p className="details__hint" role="status">
            Loading…
          </p>
        )}

        {awaitingTwoFactor && (
          <TwoFactorChallenge
            onVerified={async () => {
              setAwaitingTwoFactor(false);
              await landSignedIn('✓ Signed in. Your fleets are below.');
            }}
            onCancel={async () => {
              await signOut();
              setAwaitingTwoFactor(false);
              setAuthState({ kind: 'unauthenticated' });
              goMode('lookup');
            }}
          />
        )}

        {!awaitingTwoFactor && setPwToken !== null && (
          <SetPassword
            token={setPwToken}
            onDone={async (mfaRequired) => {
              setSetPwToken(null);
              if (mfaRequired) {
                setAwaitingTwoFactor(true);
                return;
              }
              await landSignedIn('✓ Password set. You’re signed in — your fleets are below.');
            }}
            onExpired={() => {
              setSetPwToken(null);
              goMode('lookup');
              setLookup({
                kind: 'result',
                result: {
                  kind: 'malformed',
                  message:
                    'That password link has expired or was already used. Request a fresh one below.',
                },
              });
            }}
          />
        )}

        {setPwToken === null && !awaitingTwoFactor && (
        <>
        {/* ── Signed-out: sign in and start a free fleet, both on one page ── */}
        {!signedIn && !showLookupResult && signupState.kind !== 'result' && signupState.kind !== 'editing' && (
          <Stack spacing={2.5} sx={{ width: '100%', maxWidth: 460, mx: 'auto' }}>
            <PasswordSignIn
              onSignedIn={async (mfaRequired) => {
                if (mfaRequired) {
                  setAwaitingTwoFactor(true);
                  return;
                }
                await landSignedIn('✓ Signed in. Your fleets are below.');
              }}
            />

            <Divider sx={{ color: 'text.secondary', fontSize: 14 }}>New to MobiLauncher?</Divider>

            <SignupForm
              title="Start a free fleet"
              subtitle="No card, no commitment — we seed a starter launcher and hand you a URL in under a minute."
              onSubmit={(input) => void handleSignupSubmit(input)}
              busy={signupState.kind === 'busy'}
            />

            <div className="signin__alt">
              <button type="button" className="linklike" aria-expanded={showLicenseLookup}
                onClick={() => setShowLicenseLookup((v) => !v)} data-testid="toggle-license">
                Verify a license
              </button>
            </div>
            {showLicenseLookup && (
              <LicenseLookup onSubmit={(id) => void handleLookupSubmit(id)} busy={lookup.kind === 'busy'} initialValue={initialId} />
            )}
          </Stack>
        )}

        {/* Signed-in account: the license lookup tool. */}
        {signedIn && mode === 'lookup' && !showLookupResult && (
          <LicenseLookup
            onSubmit={(id) => void handleLookupSubmit(id)}
            busy={lookup.kind === 'busy'}
            initialValue={initialId}
          />
        )}
        {mode === 'lookup' && lookup.kind === 'result' && (
          <LookupResultView result={lookup.result} onReset={() => setLookup({ kind: 'idle' })} />
        )}

        {/* Signed-in but fleetless: the "Start free" tab still provisions a fleet. */}
        {signedIn && mode === 'signup' && !showSignupResult && !showEditor && (
          <SignupForm
            onSubmit={(input) => {
              void handleSignupSubmit(input);
            }}
            busy={signupState.kind === 'busy'}
          />
        )}
        {signupFlow && signupState.kind === 'result' && (
          <SignupResultView
            result={signupState.result}
            onReset={() => setSignupState({ kind: 'idle' })}
            onCustomize={(ok) => {
              setSignupState({
                kind: 'editing',
                tenantId: ok.tenantId,
                editToken: ok.editToken,
                launcherUrl: ok.launcherUrl,
              });
              if (pendingIntent !== null) {
                setEditorInitialTab('upgrade');
                setEditorBanner({
                  kind: 'info',
                  message:
                    pendingIntent === 'upgrade-pro'
                      ? 'Pick Pro Monthly or Pro Yearly below to activate your subscription. Card details handled by Stripe.'
                      : 'Pick Enterprise Monthly or Yearly below to activate your subscription. Card details handled by Stripe.',
                });
                setPendingIntent(null);
              }
            }}
          />
        )}
        {signupFlow && signupState.kind === 'editing' && (
          <ConfigEditor
            tenantId={signupState.tenantId}
            editToken={signupState.editToken}
            launcherUrl={signupState.launcherUrl}
            role={signupState.role ?? 'owner'}
            onBack={() => setSignupState({ kind: 'idle' })}
            {...(editorInitialTab !== undefined && { initialTab: editorInitialTab })}
            {...(editorBanner !== null && { banner: editorBanner })}
          />
        )}

        {mode === 'fleets' && signedIn && signupState.kind !== 'editing' && (
          <FleetsList
            tenants={authState.tenants}
            onCustomize={({ tenantId, editToken, launcherUrl, role }) => {
              setSignupState({ kind: 'editing', tenantId, editToken, launcherUrl, role });
            }}
            onDuplicated={({ tenantId, editToken, launcherUrl }) => {
              // Refresh /me so the new tenant shows in the list on
              // back-out, then jump the user straight into the new
              // tenant's editor — saves a "find the duplicate" step. A clone is
              // owned by the same customer, so role is owner.
              void refreshMe();
              setSignupState({ kind: 'editing', tenantId, editToken, launcherUrl, role: 'owner' });
            }}
            onDeleted={() => {
              // Re-fetch /me so the deleted fleet drops out of the list.
              void refreshMe();
            }}
          />
        )}
        {mode === 'fleets' &&
          signupState.kind !== 'editing' &&
          authState.kind === 'authenticated' && (
            <TwoFactorSetup
              enabled={authState.totpEnabled}
              onChanged={() => void refreshMe()}
            />
          )}
        {mode === 'fleets' && signupState.kind === 'editing' && (
          <ConfigEditor
            tenantId={signupState.tenantId}
            editToken={signupState.editToken}
            launcherUrl={signupState.launcherUrl}
            role={signupState.role ?? 'owner'}
            onBack={() => {
              setSignupState({ kind: 'idle' });
              setEditorBanner(null);
              setEditorInitialTab(undefined);
              // Re-fetch /me — the tier may have changed if the user
              // upgraded their subscription while inside the editor.
              void refreshMe();
            }}
            {...(editorInitialTab !== undefined && { initialTab: editorInitialTab })}
            {...(editorBanner !== null && { banner: editorBanner })}
          />
        )}
        </>
        )}
      </main>

      <Box
        component="footer"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          px: 2,
          py: 1,
          borderTop: 1,
          borderColor: 'divider',
          fontSize: 13,
          color: 'text.secondary',
        }}
      >
        <span>API: <code>{apiBaseUrl}</code></span>
        <span>© {new Date().getFullYear()} Oak &amp; Ash Tech</span>
      </Box>

      {/*
        Post-signup tour. Targets render only when the SignupResult
        screen is mounted; the Tour itself gates on a localStorage
        "seen" flag and gracefully no-ops once the user has finished
        or skipped it on a previous run.
      */}
      <Tour
        id={POST_SIGNUP_TOUR_ID}
        steps={POST_SIGNUP_STEPS}
        run={showSignupResult}
      />
    </div>
  );
}

/**
 * A top-level tab — a real `<a href="#…">` link so the URL is shareable and the
 * browser/mouse Back-Forward work, with the click intercepted to route in-SPA.
 * Modified clicks (cmd/ctrl/middle) fall through to open a new tab.
 */
function ModeTab({
  mode,
  active,
  children,
}: {
  mode: Mode;
  active: boolean;
  children: string;
}): JSX.Element {
  return (
    <a
      role="tab"
      aria-selected={active}
      className={`portal__tab${active ? ' portal__tab--active' : ''}`}
      href={modeHref(mode)}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        goMode(mode);
      }}
    >
      {children}
    </a>
  );
}

function LookupResultView({
  result,
  onReset,
}: {
  result: FetchResult;
  onReset: () => void;
}): JSX.Element {
  switch (result.kind) {
    case 'ok':
      return (
        <LicenseDetails license={result.license} rawBlob={result.rawBlob} onLookupAnother={onReset} />
      );
    case 'not-found':
      return (
        <ErrorBlock
          title="License not found"
          body="No active license matches that ID. Check your purchase email or contact support."
          onReset={onReset}
        />
      );
    case 'revoked':
      return (
        <ErrorBlock
          title="License revoked"
          body="This license has been revoked. If you believe this is an error, contact support."
          onReset={onReset}
        />
      );
    case 'malformed':
      return (
        <ErrorBlock title="Malformed response" body={result.message} onReset={onReset} />
      );
    case 'network':
      return (
        <ErrorBlock
          title="Couldn’t reach the licensing server"
          body={`${result.message}. The portal is configured to use ${apiBaseUrl} — make sure that endpoint is up and reachable from your network.`}
          onReset={onReset}
        />
      );
  }
}

function SignupResultView({
  result,
  onReset,
  onCustomize,
}: {
  result: SignupApiResult;
  onReset: () => void;
  onCustomize: (ok: Extract<SignupApiResult, { kind: 'ok' }>) => void;
}): JSX.Element {
  switch (result.kind) {
    case 'ok':
      return (
        <SignupResult
          tenantId={result.tenantId}
          launcherUrl={result.launcherUrl}
          isNew={result.isNew}
          onSignupAnother={onReset}
          onCustomize={() => onCustomize(result)}
        />
      );
    case 'invalid':
      return (
        <ErrorBlock
          title="Check the form"
          body={result.message}
          onReset={onReset}
        />
      );
    case 'network':
      return (
        <ErrorBlock
          title="Couldn’t reach the signup endpoint"
          body={`${result.message}. The portal is configured to use ${apiBaseUrl} — make sure that endpoint is up and reachable from your network.`}
          onReset={onReset}
        />
      );
    case 'unexpected':
      return (
        <ErrorBlock
          title={`Server returned ${String(result.status)}`}
          body={result.message}
          onReset={onReset}
        />
      );
  }
}

function ErrorBlock({
  title,
  body,
  onReset,
}: {
  title: string;
  body: string;
  onReset: () => void;
}): JSX.Element {
  return (
    <section className="error" role="alert" data-testid="portal-error">
      <h2 className="error__title">{title}</h2>
      <p className="error__body">{body}</p>
      <button type="button" className="error__again" onClick={onReset}>
        Try again
      </button>
    </section>
  );
}
